import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

type ToastTone = "info" | "success" | "error";

interface ToastMessage {
  id: number;
  text: string;
  tone: ToastTone;
}

interface ToastApi {
  showToast: (text: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastApi | null>(null);
const MAX_TOASTS = 2;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  const timersRef = useRef<Map<number, number>>(new Map());

  const showToast = useCallback((text: string, tone: ToastTone = "info") => {
    const id = Date.now() + Math.random();
    setMessages((current) => {
      const withoutSame = current.filter((item) => !(item.text === text && item.tone === tone));
      const next = [...withoutSame, { id, text, tone }];
      return next.slice(-MAX_TOASTS);
    });
    const handle = window.setTimeout(() => {
      setMessages((current) => current.filter((item) => item.id !== id));
      timersRef.current.delete(id);
    }, 3200);
    timersRef.current.set(id, handle);
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="react-toast-stack" aria-live="polite" aria-atomic="false">
        {messages.map((message) => (
          <div className={`react-toast ${message.tone}`} role="status" key={message.id}>
            {message.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const value = useContext(ToastContext);
  if (!value) throw new Error("useToast must be used inside ToastProvider");
  return value;
}
